import React from "react";
import { Link } from "react-router-dom";
import { CmsImage, CmsPage } from "../types/cms";

type HeaderProps = {
  siteTitle: string;
  siteLogo?: CmsImage;
  startPage?: CmsPage;
  eventsPageTitle?: string;
  newsPageTitle?: string;
  pages?: CmsPage[];
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
};

const organizePages = (pages: CmsPage[]) => {
  const topLevelPages: CmsPage[] = [];
  const childPages: { [key: string]: CmsPage[] } = {};

  pages
    .filter((p) => p.showInMenu)
    .sort((a, b) => a.order - b.order)
    .forEach((page) => {
      if (!page.parentPage) {
        topLevelPages.push(page);
      } else {
        if (!childPages[page.parentPage]) {
          childPages[page.parentPage] = [];
        }
        childPages[page.parentPage].push(page);
      }
    });

  return { topLevelPages, childPages };
};

const SubmenuDropdown = ({
  parentTitle,
  parentSlug,
  children,
}: {
  parentTitle: string;
  parentSlug: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div
      className="relative group flex items-center gap-1"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <Link
        to={`/${parentSlug}`}
        className="text-topMenu font-topMenu text-light-textOnSecondary hover:text-light--textOnSecondary py-2"
      >
        {parentTitle}
      </Link>
      <button
        className="p-1 text-light-textOnSecondary hover:text-light--textOnSecondary"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle submenu"
      >
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <div
        className={`absolute left-0 top-full w-auto bg-light-backgroundHeader rounded-md shadow-lg py-1 z-50 transition-opacity duration-150 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

const MobileSubmenu = ({
  parentTitle,
  parentSlug,
  children,
  onNavigate,
}: {
  parentTitle: string;
  parentSlug: string;
  children: React.ReactNode;
  onNavigate: () => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="w-full">
      <div className="w-full px-5 py-2 flex items-center justify-between">
        <Link
          to={`/${parentSlug}`}
          className="text-lg font-topMenu text-topMenu text-light-textOnSecondary hover:text-light-textOnSecondary"
          onClick={onNavigate}
        >
          {parentTitle}
        </Link>
        <button
          className="p-1 text-light-textOnSecondary hover:text-light-textOnSecondary"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          aria-label="Toggle submenu"
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ease-in-out ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          isOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="bg-light-backgroundHeader">
          <div className="pl-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

const Header = ({
  siteTitle,
  siteLogo,
  startPage,
  pages = [],
  isMenuOpen,
  setIsMenuOpen,
}: HeaderProps) => {
  const { topLevelPages, childPages } = React.useMemo(
    () => organizePages(pages),
    [pages]
  );

  const renderDesktopMenu = () => (
    <div className="hidden md:flex justify-after items-center gap-10">
      {topLevelPages.map((page) =>
        childPages[page.title] ? (
          <SubmenuDropdown
            key={page.slug}
            parentTitle={page.title}
            parentSlug={page.slug}
          >
            {childPages[page.title].map((childPage) => (
              <Link
                key={childPage.slug}
                to={`/${childPage.slug}`}
                className={`block px-4 py-2 font-topMenu text-topMenu ${
                  window.location.pathname === `/${childPage.slug}`
                    ? "text-light-textOnSecondary"
                    : "text-light-textOnSecondary hover:text-light-textOnSecondary opacity-80 hover:opacity-100"
                }`}
              >
                {childPage.menuTitle || childPage.title}
              </Link>
            ))}
          </SubmenuDropdown>
        ) : (
          <Link
            key={page.slug}
            to={`/${page.slug}`}
            className={`font-topMenu text-topMenu text-light-textOnSecondary hover:text-light-textOnSecondary`}
          >
            {page.menuTitle || page.title}
          </Link>
        )
      )}
    </div>
  );

  const renderMobileMenu = () => (
    <>
      <div
        className={`fixed inset-0 w-full h-full bg-black transition-all duration-300 ease-in-out ${
          isMenuOpen ? "opacity-50 visible" : "opacity-0 invisible"
        }`}
        style={{ zIndex: 45 }}
        onClick={() => setIsMenuOpen(false)}
      />
      <div
        className={`fixed inset-y-0 right-0 transform ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out w-64 bg-light-backgroundHeader shadow-lg z-50`}
      >
        <div className="h-full flex flex-col">
          <div className="flex justify-end p-4">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-light-textOnSecondary"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {topLevelPages.map((page) =>
              childPages[page.title] ? (
                <MobileSubmenu
                  key={page.slug}
                  parentTitle={page.title}
                  parentSlug={page.slug}
                  onNavigate={() => setIsMenuOpen(false)}
                >
                  {childPages[page.title].map((childPage) => (
                    <Link
                      key={childPage.slug}
                      to={`/${childPage.slug}`}
                      className={`block px-5 py-2 font-topMenu text-topMenu ${
                        window.location.pathname === `/${childPage.slug}`
                          ? "text-light-textOnSecondary"
                          : "text-light-textOnSecondary hover:text-light-textOnSecondary opacity-80 hover:opacity-100"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {childPage.menuTitle || childPage.title}
                    </Link>
                  ))}
                </MobileSubmenu>
              ) : (
                <Link
                  key={page.slug}
                  to={`/${page.slug}`}
                  className={`block px-5 py-2 font-topMenu text-topMenu ${
                    window.location.pathname === `/${page.slug}`
                      ? "text-light-textOnSecondary"
                      : "text-light-textOnSecondary hover:text-light-textOnSecondary opacity-80 hover:opacity-100"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {page.menuTitle || page.title}
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <header className="container mx-auto max-w-screen-lg">
      <div className="flex justify-left p-5 bg-light">
        {siteLogo ? (
          <img src={siteLogo.image} alt={siteLogo.alt} className="h-32" />
        ) : (
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl p-5 text-light-textOnSecondary break-words max-w-full">
            {siteTitle}
          </h1>
        )}
      </div>
      <nav className="px-8 xl:px-5 p-5 lb:py-8 bg-light">
        <div className="md:hidden flex justify-between items-center">
          {startPage && (
            <>
              <Link
                to="/"
                className="text-topMenu font-topMenu text-light-textOnSecondary hover:text-light--textOnSecondary"
              >
                {startPage.menuTitle || startPage.title}
              </Link>
              <button
                aria-label="Toggle Menu"
                className="ml-auto rounded-md px-2 py-1 text-light-textOnSecondary hover:text-light-textOnSecondary focus:outline-none md:hidden"
                onClick={(e) => {
                  setIsMenuOpen(true);
                  e.stopPropagation();
                }}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </>
          )}
        </div>
        {renderDesktopMenu()}
        {renderMobileMenu()}
      </nav>
    </header>
  );
};

export default Header;
